/**
 * Build an x-axis tick formatter that adapts to the data range.
 *
 *   daily/weekly bucket  (`YYYY-MM-DD`)  spans 1 year → "DD MMM"
 *   daily/weekly bucket                  spans >1 yr → "DD MMM YY"
 *   monthly bucket       (`YYYY-MM`)     spans 1 year → "MMM"
 *   monthly bucket                       spans >1 yr → "MMM YY"
 *
 * Pass it together with `tickNumber` so MUI X Charts also thins out
 * the ticks instead of trying to render one per data point.
 */
const DAY_NO_YEAR    = new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short' })
const DAY_WITH_YEAR  = new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short', year: '2-digit' })
const MON_NO_YEAR    = new Intl.DateTimeFormat('es-ES', { month: 'short' })
const MON_WITH_YEAR  = new Intl.DateTimeFormat('es-ES', { month: 'short', year: '2-digit' })

export function buildTickFormatter(periods) {
	if (!periods?.length) return (s) => s || ''
	// Detect bucket size from the first label.
	const isDaily = (periods[0] || '').length === 10
	// Detect multi-year range from the unique YYYY prefixes.
	const years = new Set(periods.map(p => (p || '').slice(0, 4)))
	const showYear = years.size > 1

	const fmt = isDaily
		? (showYear ? DAY_WITH_YEAR : DAY_NO_YEAR)
		: (showYear ? MON_WITH_YEAR : MON_NO_YEAR)

	return (s) => {
		if (!s) return ''
		const d = new Date(isDaily ? s : `${s}-01`)
		if (isNaN(d)) return s
		return fmt.format(d)
	}
}

/**
 * Pick a sensible tickNumber so the axis doesn't try to render N labels
 * for N points. ~12 labels reads cleanly at typical dashboard widths.
 */
export function pickTickNumber(periods) {
	const n = periods?.length || 0
	if (n <= 12) return n        // show all
	if (n <= 30) return 10
	if (n <= 90) return 12
	if (n <= 180) return 14
	return 18
}
